import type { Dispatch } from '@reduxjs/toolkit';
import type {
  IGetPlacementListQueryParams,
  INewAndUpdatePlacementDto,
  IPlacementState,
} from 'src/legacy/@types/placement';

import { createSlice } from '@reduxjs/toolkit';
import axios from 'src/legacy/utils/axios';

// ----------------------------------------------------------------------

const initialState: IPlacementState = {
  actionStatus: {
    create: 'none',
    delete: 'none',
    update: 'none',
  },
  //
  agreementLegalBases: null,

  error: null,

  isLoading: false,

  placementCharacters: null,

  placementPurposes: null,
  placementReasons: null,
  placements: null,
  placementTypes: null,
  selectedPlacement: null,
  terminationReasons: null,
};

const slice = createSlice({
  initialState,
  name: 'placement',
  reducers: {
    // CLEAR ERROR
    clearError(state) {
      state.error = '';
    },

    // CREATE NEW PLACEMENT
    createPlacementSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        create: 'complete',
      };
      state.error = null;
      state.placements = [...(state.placements || []), action.payload];
    },

    // DELETE PLACEMENT
    deletePlacementSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        delete: 'complete',
      };
      if (state.placements) {
        state.placements = state.placements.filter(
          (placement) => !action.payload.placementIds.includes(placement.id)
        );
      }
    },

    // GET AGREEMENT LEGAL BASIS LIST
    getAgreementLegalBasisListSuccess(state, action) {
      state.agreementLegalBases = action.payload;
    },

    // GET PLACEMENT CHARACTER LIST
    getPlacementCharacterListSuccess(state, action) {
      state.placementCharacters = action.payload;
    },

    // GET PLACEMENTS
    getPlacementListSuccess(state, action) {
      state.isLoading = false;
      state.placements = action.payload;
    },

    // GET PLACEMENT PURPOSE LIST
    getPlacementPurposeListSuccess(state, action) {
      state.placementPurposes = action.payload;
    },

    // GET PLACEMENT REASON LIST
    getPlacementReasonListSuccess(state, action) {
      state.placementReasons = action.payload;
    },

    // GET PLACEMENT
    getPlacementStart(state) {
      state.isLoading = false;
      state.selectedPlacement = null;
    },

    getPlacementSuccess(state, action) {
      state.isLoading = false;
      state.selectedPlacement = action.payload;
    },

    // GET PLACEMENT TYPE LIST
    getPlacementTypeListSuccess(state, action) {
      state.placementTypes = action.payload;
    },

    // GET TERMINATION REASON LIST
    getTerminationReasonListSuccess(state, action) {
      state.terminationReasons = action.payload;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // UPDATE ACTION STATUS
    updateActionState(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        ...action.payload,
      };
    },

    // UPDATE PLACEMENT
    updatePlacementSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        update: 'complete',
      };
      state.error = null;
      if (state.placements) {
        state.placements = state.placements.map((placement) => {
          if (placement.id === action.payload.id) {
            return action.payload;
          }
          return placement;
        });
      }
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const { updateActionState } = slice.actions;

// ----------------------------------------------------------------------
// --------------              Placement Actions           --------------
// ----------------------------------------------------------------------

export const getPlacementList = (params: IGetPlacementListQueryParams) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(`/arrangement/placements/`, { params });
      let allResults: any[] = response.data.results;

      if (
        response.data.count > allResults.length ||
        response.data.next !== ''
      ) {
        await Promise.all(
          Array(Math.ceil(response.data.count / 20) - 1)
            .fill(0)
            .map(async (_, index) => {
              const response2 = await axios.get(`/arrangement/placements/`, {
                params: { ...params, page: index + 2 },
              });
              allResults = [...allResults, ...response2.data.results];
            })
        );
      }

      dispatch(slice.actions.getPlacementListSuccess(allResults));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const createPlacement = (
  companyId: string,
  newPlacement: INewAndUpdatePlacementDto
) => {
  return async (dispatch: Dispatch) => {
    dispatch(
      slice.actions.updateActionState({ create: 'loading', error: null })
    );
    try {
      const response = await axios.post(
        `/arrangement/placements/?company=${companyId}`,
        newPlacement
      );
      dispatch(slice.actions.createPlacementSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.updateActionState({ create: 'failed' }));
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const getPlacement = (placementId: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.getPlacementStart());
    try {
      const response = await axios.get(
        `/arrangement/placements/${placementId}/`
      );
      dispatch(slice.actions.getPlacementSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const updatePlacement = (
  placementId: number,
  updatedPlacement: INewAndUpdatePlacementDto
) => {
  return async (dispatch: Dispatch) => {
    dispatch(
      slice.actions.updateActionState({ error: null, update: 'loading' })
    );
    try {
      const response = await axios.put(
        `/arrangement/placements/${placementId}/`,
        updatedPlacement
      );
      dispatch(slice.actions.updatePlacementSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.updateActionState({ update: 'failed' }));
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const deletePlacement = (placementIds: number[]) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.updateActionState({ delete: 'loading' }));
    try {
      await Promise.all(
        placementIds.map(async (id) => {
          await axios.delete(`/arrangement/placements/${id}/`);
        })
      );
      dispatch(slice.actions.deletePlacementSuccess({ placementIds }));
    } catch (error) {
      console.log('error: ', error);
      dispatch(slice.actions.updateActionState({ delete: 'failed' }));
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------
// --------------         Termination Reason Actions       --------------
// ----------------------------------------------------------------------

export const getTerminationReasonList = (paginated = false) => {
  return async (dispatch: Dispatch) => {
    try {
      const response = await axios.get(
        `/arrangement/termination-reasons/?paginated=${paginated}`
      );
      dispatch(
        slice.actions.getTerminationReasonListSuccess(
          response.data?.results || response.data
        )
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------
// --------------           Placement Type Actions         --------------
// ----------------------------------------------------------------------

export const getPlacementTypesList = (paginated = false, company: string) => {
  return async (dispatch: Dispatch) => {
    try {
      const response = await axios.get(
        `/arrangement/placement-types/?paginated=${paginated}`,
        { params: { company } }
      );
      dispatch(
        slice.actions.getPlacementTypeListSuccess(
          response.data?.results || response.data
        )
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------
// --------------          Placement Reason Actions        --------------
// ----------------------------------------------------------------------

export const getPlacementReasonList = (paginated = false) => {
  return async (dispatch: Dispatch) => {
    try {
      const response = await axios.get(
        `/arrangement/placement-reasons/?paginated=${paginated}`
      );
      dispatch(
        slice.actions.getPlacementReasonListSuccess(
          response.data?.results || response.data
        )
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------
// --------------          Placement Purpose Actions       --------------
// ----------------------------------------------------------------------

export const getPlacementPurposeList = (paginated = false) => {
  return async (dispatch: Dispatch) => {
    try {
      const response = await axios.get(
        `/arrangement/placement-purposes/?paginated=${paginated}`
      );
      dispatch(
        slice.actions.getPlacementPurposeListSuccess(
          response.data?.results || response.data
        )
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------
// --------------         Placement Character Actions      --------------
// ----------------------------------------------------------------------

export const getPlacementCharacterList = (paginated = false) => {
  return async (dispatch: Dispatch) => {
    try {
      const response = await axios.get(
        `/arrangement/placement-characters/?paginated=${paginated}`
      );
      dispatch(
        slice.actions.getPlacementCharacterListSuccess(
          response.data?.results || response.data
        )
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------
// --------------        Agreement Legal Basis Actions     --------------
// ----------------------------------------------------------------------

export const getAgreementLegalBasisList = (paginated = false) => {
  return async (dispatch: Dispatch) => {
    try {
      const response = await axios.get(
        `/arrangement/agreement-legal-bases/?paginated=${paginated}`
      );
      dispatch(
        slice.actions.getAgreementLegalBasisListSuccess(
          response.data?.results || response.data
        )
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};
